<div class="create-invoice-modal">
  <form class="create-invoice-modal__form" [formGroup]="form" (ngSubmit)="submitForm()">
    <section class="create-invoice-modal__panel">
      <div class="create-invoice-modal__grid">
        <div class="create-invoice-modal__field md:col-span-2">
          <label class="form-label" for="invoice-customer">
            {{ 'COMPONENTS.INVOICES.FIELDS.CUSTOMER' | translate }}
          </label>
          <app-search
            id="invoice-customer"
            customClass="form-search-input"
            formControlName="customerId"
            [items]="(clientOptions$ | async) ?? []"
            [filterFn]="clientFilterFn"
            [formatter]="clientFormatter"
            [compareFn]="clientCompareFn"
            [placeholderText]="'COMPONENTS.INVOICES.PLACEHOLDERS.CUSTOMER' | translate"
            [errorMessage]="'COMPONENTS.INVOICES.ERRORS.INVALID_CUSTOMER' | translate"
            [resultTemplate]="customerOptionTemplate"
          ></app-search>

          <ng-template #customerOptionTemplate let-r="result" let-t="term">
            @if (r) {
              <div class="create-invoice-modal__option">
                <span
                  class="create-invoice-modal__option-badge"
                  [class.create-invoice-modal__option-badge--organization]="r.type === 'organization'"
                  [class.create-invoice-modal__option-badge--contact]="r.type === 'contact'"
                >
                  {{ r.typeLabelKey | translate }}
                </span>
                <div class="create-invoice-modal__option-text">
                  <ngb-highlight
                    class="create-invoice-modal__option-title"
                    highlightClass="hover-highlight"
                    [result]="r.title"
                    [term]="t"
                  ></ngb-highlight>
                  @if (r.subtitle) {
                    <ngb-highlight
                      class="create-invoice-modal__option-subtitle"
                      highlightClass="hover-highlight"
                      [result]="r.subtitle"
                      [term]="t"
                    ></ngb-highlight>
                  }
                </div>
              </div>
            }
          </ng-template>
        </div>

        <div class="create-invoice-modal__field">
          <label class="form-label" for="invoice-date">
            {{ 'COMPONENTS.INVOICES.FIELDS.INVOICE_DATE' | translate }}
          </label>
          <input id="invoice-date" type="date" class="form-input" formControlName="invoiceDate" />
        </div>

        <div class="create-invoice-modal__field">
          <label class="form-label" for="invoice-due-date">
            {{ 'COMPONENTS.INVOICES.FIELDS.DUE_DATE' | translate }}
          </label>
          <input id="invoice-due-date" type="date" class="form-input" formControlName="dueDate" />
        </div>
      </div>
    </section>

    <section class="create-invoice-modal__panel create-invoice-modal__panel--lines">
      <div class="create-invoice-modal__lines-header">
        <p class="create-invoice-modal__section-hint">
          {{ 'COMPONENTS.INVOICES.MODAL.LINES_HINT' | translate }}
        </p>

        <button type="button" class="create-invoice-modal__add-button" (click)="addLine()">
          + {{ 'COMPONENTS.INVOICES.BUTTONS.ADD_LINE' | translate }}
        </button>
      </div>

      @if (lineForms.length === 0) {
        <div class="create-invoice-modal__empty-lines">
          {{ 'COMPONENTS.INVOICES.MODAL.EMPTY_LINES' | translate }}
        </div>
      } @else {
        <div class="create-invoice-modal__lines" formArrayName="lines">
          @for (lineForm of lineForms.controls; track lineForm; let lineIndex = $index) {
            <article class="create-invoice-modal__line" [formGroupName]="lineIndex">
              <div class="create-invoice-modal__line-header">
                <span class="create-invoice-modal__line-index">
                  {{ 'COMPONENTS.INVOICES.MODAL.LINE_NUMBER' | translate }} {{ lineIndex + 1 }}
                </span>
                <button
                  type="button"
                  class="create-invoice-modal__line-remove"
                  (click)="removeLine(lineIndex)"
                >
                  {{ 'BUTTONS.REMOVE' | translate }}
                </button>
              </div>

              <div class="create-invoice-modal__line-grid">
                <div class="create-invoice-modal__field create-invoice-modal__field--wide">
                  <label class="form-label" [attr.for]="'invoice-line-item-' + lineIndex">
                    {{ 'COMPONENTS.INVOICES.FIELDS.ITEM' | translate }}
                  </label>
                  <app-search
                    [id]="'invoice-line-item-' + lineIndex"
                    customClass="form-search-input"
                    formControlName="inventoryItemId"
                    [items]="(inventoryItemOptions$ | async) ?? []"
                    [filterFn]="inventoryItemFilterFn"
                    [formatter]="inventoryItemFormatter"
                    [compareFn]="inventoryItemCompareFn"
                    [placeholderText]="'COMPONENTS.INVOICES.PLACEHOLDERS.ITEM' | translate"
                    [errorMessage]="'COMPONENTS.INVOICES.ERRORS.INVALID_ITEM' | translate"
                    [resultTemplate]="itemOptionTemplate"
                    (ngModelChange)="onLineItemChange(lineIndex, $event)"
                  ></app-search>

                  <ng-template #itemOptionTemplate let-r="result" let-t="term">
                    @if (r) {
                      <div class="create-invoice-modal__option">
                        <div class="create-invoice-modal__option-text">
                          <ngb-highlight
                            class="create-invoice-modal__option-title"
                            highlightClass="hover-highlight"
                            [result]="r.title"
                            [term]="t"
                          ></ngb-highlight>
                          @if (r.subtitle) {
                            <ngb-highlight
                              class="create-invoice-modal__option-subtitle"
                              highlightClass="hover-highlight"
                              [result]="r.subtitle"
                              [term]="t"
                            ></ngb-highlight>
                          }
                        </div>
                      </div>
                    }
                  </ng-template>
                </div>

                <label class="create-invoice-modal__field">
                  <span class="form-label">
                    {{ 'COMPONENTS.INVOICES.FIELDS.QUANTITY' | translate }}
                  </span>
                  <input type="number" min="0.0001" step="0.0001" class="form-input" formControlName="quantity" />
                </label>

                <label class="create-invoice-modal__field">
                  <span class="form-label">
                    {{ 'COMPONENTS.INVOICES.FIELDS.UNIT' | translate }}
                  </span>
                  <select class="form-input" formControlName="unitOfMeasureId">
                    <option [ngValue]="null">
                      {{ 'COMPONENTS.INVOICES.PLACEHOLDERS.UNIT' | translate }}
                    </option>
                    @for (uom of (unitsOfMeasure$ | async) ?? []; track uom.id) {
                      <option [ngValue]="uom.id">{{ uom.displayName }}</option>
                    }
                  </select>
                </label>

                <label class="create-invoice-modal__field">
                  <span class="form-label">
                    {{ 'COMPONENTS.INVOICES.FIELDS.PRICE' | translate }}
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    class="form-input"
                    formControlName="unitPrice"
                    [placeholder]="'COMPONENTS.INVOICES.PLACEHOLDERS.PRICE' | translate"
                  />
                </label>

                <label class="create-invoice-modal__field">
                  <span class="form-label">
                    {{ 'COMPONENTS.INVOICES.FIELDS.TAX_PERCENT' | translate }}
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.0001"
                    class="form-input"
                    formControlName="taxPercent"
                    [placeholder]="'COMPONENTS.INVOICES.PLACEHOLDERS.TAX_PERCENT' | translate"
                  />
                </label>

                <label class="create-invoice-modal__field create-invoice-modal__field--wide">
                  <span class="form-label">
                    {{ 'COMPONENTS.INVOICES.FIELDS.DESCRIPTION' | translate }}
                  </span>
                  <input type="text" class="form-input" formControlName="description" />
                </label>
              </div>

              @if (lineRows[lineIndex]?.errorKey) {
                <p class="create-invoice-modal__line-error" role="alert">
                  {{ lineRows[lineIndex].errorKey | translate }}
                </p>
              }
            </article>
          }
        </div>
      }

      @if (errorMessageKey) {
        <p class="create-invoice-modal__error" role="alert">
          {{ errorMessageKey | translate }}
        </p>
      }
    </section>

    <div class="create-project-actions create-invoice-modal__actions">
      <button type="button" class="form-secondary-btn" [disabled]="isSubmitting" (click)="onCancel()">
        {{ 'BUTTONS.CANCEL' | translate }}
      </button>
      <button type="submit" class="form-primary-btn" [disabled]="isSubmitting">
        {{ (isSubmitting ? 'COMPONENTS.INVOICES.BUTTONS.CREATING' : 'COMPONENTS.INVOICES.BUTTONS.CREATE') | translate }}
      </button>
    </div>
  </form>
</div>
